/**
 * Document Mock Data
 *
 * Sample documents for the Document Vault (FR-003).
 * Represents various document types used in halal certification.
 */

import type { DocumentType } from '../types/application';

/**
 * Document category for organization
 */
export enum DocumentCategory {
  LICENSES = 'LICENSES',               // Business licenses
  POLICIES = 'POLICIES',               // Policies and procedures
  CERTIFICATIONS = 'CERTIFICATIONS',   // Certifications (halal, ISO, etc.)
  FACILITY = 'FACILITY',               // Facility-related docs
  TRAINING = 'TRAINING',               // Training records
  SUPPLIERS = 'SUPPLIERS',             // Supplier documents
  PRODUCTS = 'PRODUCTS',               // Product specifications
  AUDITS = 'AUDITS',                   // Audit reports
  SOPs = 'SOPs',                       // Standard Operating Procedures
  OTHER = 'OTHER',                     // Miscellaneous
}

/**
 * Document access level
 */
export enum DocumentAccessLevel {
  PUBLIC = 'PUBLIC',         // Visible to all
  COMPANY = 'COMPANY',       // Company members only
  ADMIN_ONLY = 'ADMIN_ONLY', // Admins only
  CONFIDENTIAL = 'CONFIDENTIAL', // Highly restricted
}

/**
 * Document status
 */
export enum DocumentStatus {
  DRAFT = 'DRAFT',             // Being prepared
  ACTIVE = 'ACTIVE',           // Current version
  ARCHIVED = 'ARCHIVED',       // Superseded
  EXPIRED = 'EXPIRED',         // Expired
  PENDING_REVIEW = 'PENDING_REVIEW', // Awaiting verification
}

/**
 * Document version information
 */
export interface DocumentVersion {
  version: string;
  uploadedAt: Date;
  uploadedBy: string;
  uploadedByName: string;
  fileSize: number;
  url: string;
  changes?: string; // What changed in this version
}

/**
 * Document in the vault
 */
export interface Document {
  id: string;
  name: string;
  category: DocumentCategory;
  type?: DocumentType; // Maps to application document types
  description?: string;

  // File information
  fileName: string;
  fileSize: number; // bytes
  mimeType: string;
  url: string;

  // Versioning
  currentVersion: string;
  versions: DocumentVersion[];

  // Organization
  companyId: string;
  companyName: string;

  // Access and status
  accessLevel: DocumentAccessLevel;
  status: DocumentStatus;

  // Dates and expiry
  uploadedAt: Date;
  uploadedBy: string;
  uploadedByName: string;
  expiryDate?: Date;
  reminderDays?: number; // Days before expiry to send reminder

  // Verification
  verified: boolean;
  verifiedBy?: string;
  verifiedByName?: string;
  verifiedAt?: Date;

  // Tags for search
  tags: string[];

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Mock documents
 */
export const mockDocuments: Document[] = [
  // Licenses
  {
    id: 'doc-vault-001',
    name: 'SSM Business Registration Certificate',
    category: 'LICENSES' as DocumentCategory,
    type: 'BUSINESS_LICENSE' as DocumentType,
    description: 'Malaysian business registration with SSM',
    fileName: 'ssm-registration-2024.pdf',
    fileSize: 524288, // 512 KB
    mimeType: 'application/pdf',
    url: '/mock/vault/ssm-registration-2024.pdf',
    currentVersion: '1.0',
    versions: [
      {
        version: '1.0',
        uploadedAt: new Date('2024-01-15T10:00:00'),
        uploadedBy: 'user-001',
        uploadedByName: 'Ahmad bin Ibrahim',
        fileSize: 524288,
        url: '/mock/vault/ssm-registration-2024.pdf',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'COMPANY' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2024-01-15T10:00:00'),
    uploadedBy: 'user-001',
    uploadedByName: 'Ahmad bin Ibrahim',
    expiryDate: new Date('2025-12-31T23:59:59'),
    reminderDays: 30,
    verified: true,
    verifiedBy: 'user-007',
    verifiedByName: 'Sarah Lee',
    verifiedAt: new Date('2024-01-16T14:00:00'),
    tags: ['license', 'ssm', 'business', 'legal'],
    createdAt: new Date('2024-01-15T10:00:00'),
    updatedAt: new Date('2024-01-16T14:00:00'),
  },

  // Policies
  {
    id: 'doc-vault-002',
    name: 'Halal Management Policy',
    category: 'POLICIES' as DocumentCategory,
    type: 'HALAL_POLICY' as DocumentType,
    description: 'Company-wide halal management policy and commitment',
    fileName: 'halal-policy-v2.1.pdf',
    fileSize: 1048576, // 1 MB
    mimeType: 'application/pdf',
    url: '/mock/vault/halal-policy-v2.1.pdf',
    currentVersion: '2.1',
    versions: [
      {
        version: '1.0',
        uploadedAt: new Date('2023-06-01T09:00:00'),
        uploadedBy: 'user-001',
        uploadedByName: 'Ahmad bin Ibrahim',
        fileSize: 983040,
        url: '/mock/vault/halal-policy-v1.0.pdf',
      },
      {
        version: '2.0',
        uploadedAt: new Date('2024-06-01T09:00:00'),
        uploadedBy: 'user-001',
        uploadedByName: 'Ahmad bin Ibrahim',
        fileSize: 1024000,
        url: '/mock/vault/halal-policy-v2.0.pdf',
        changes: 'Updated to align with MHMS 2020 requirements',
      },
      {
        version: '2.1',
        uploadedAt: new Date('2024-11-15T14:30:00'),
        uploadedBy: 'user-001',
        uploadedByName: 'Ahmad bin Ibrahim',
        fileSize: 1048576,
        url: '/mock/vault/halal-policy-v2.1.pdf',
        changes: 'Minor corrections and added digital signatures',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'PUBLIC' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2024-11-15T14:30:00'),
    uploadedBy: 'user-001',
    uploadedByName: 'Ahmad bin Ibrahim',
    verified: true,
    verifiedBy: 'user-003',
    verifiedByName: 'Dr. Zainab Ali',
    verifiedAt: new Date('2024-11-16T10:00:00'),
    tags: ['policy', 'halal', 'mhms', 'management'],
    createdAt: new Date('2023-06-01T09:00:00'),
    updatedAt: new Date('2024-11-16T10:00:00'),
  },

  // Certifications
  {
    id: 'doc-vault-003',
    name: 'ISO 22000:2018 Certificate',
    category: 'CERTIFICATIONS' as DocumentCategory,
    description: 'Food Safety Management System certification',
    fileName: 'iso-22000-certificate.pdf',
    fileSize: 786432, // 768 KB
    mimeType: 'application/pdf',
    url: '/mock/vault/iso-22000-certificate.pdf',
    currentVersion: '1.0',
    versions: [
      {
        version: '1.0',
        uploadedAt: new Date('2023-03-20T11:00:00'),
        uploadedBy: 'user-001',
        uploadedByName: 'Ahmad bin Ibrahim',
        fileSize: 786432,
        url: '/mock/vault/iso-22000-certificate.pdf',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'PUBLIC' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2023-03-20T11:00:00'),
    uploadedBy: 'user-001',
    uploadedByName: 'Ahmad bin Ibrahim',
    expiryDate: new Date('2026-03-19T23:59:59'),
    reminderDays: 90,
    verified: true,
    verifiedBy: 'user-007',
    verifiedByName: 'Sarah Lee',
    verifiedAt: new Date('2023-03-21T09:00:00'),
    tags: ['iso', 'food-safety', 'certification', 'quality'],
    createdAt: new Date('2023-03-20T11:00:00'),
    updatedAt: new Date('2023-03-21T09:00:00'),
  },

  // Facility
  {
    id: 'doc-vault-004',
    name: 'Factory Floor Plan 2025',
    category: 'FACILITY' as DocumentCategory,
    type: 'FACILITY_LAYOUT' as DocumentType,
    description: 'Updated facility layout with new production line',
    fileName: 'facility-layout-2025.pdf',
    fileSize: 2097152, // 2 MB
    mimeType: 'application/pdf',
    url: '/mock/vault/facility-layout-2025.pdf',
    currentVersion: '3.0',
    versions: [
      {
        version: '1.0',
        uploadedAt: new Date('2022-01-10T08:00:00'),
        uploadedBy: 'user-001',
        uploadedByName: 'Ahmad bin Ibrahim',
        fileSize: 1572864,
        url: '/mock/vault/facility-layout-v1.0.pdf',
      },
      {
        version: '2.0',
        uploadedAt: new Date('2023-06-15T10:00:00'),
        uploadedBy: 'user-001',
        uploadedByName: 'Ahmad bin Ibrahim',
        fileSize: 1835008,
        url: '/mock/vault/facility-layout-v2.0.pdf',
        changes: 'Added cold storage section',
      },
      {
        version: '3.0',
        uploadedAt: new Date('2025-01-05T14:00:00'),
        uploadedBy: 'user-002',
        uploadedByName: 'Siti Aminah',
        fileSize: 2097152,
        url: '/mock/vault/facility-layout-2025.pdf',
        changes: 'New packaging line and expanded warehouse',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'COMPANY' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2025-01-05T14:00:00'),
    uploadedBy: 'user-002',
    uploadedByName: 'Siti Aminah',
    verified: false,
    tags: ['facility', 'layout', 'floor-plan', 'production'],
    createdAt: new Date('2022-01-10T08:00:00'),
    updatedAt: new Date('2025-01-05T14:00:00'),
  },

  // SOPs
  {
    id: 'doc-vault-005',
    name: 'SOP Manual - Production',
    category: 'SOPs' as DocumentCategory,
    type: 'SOP_MANUAL' as DocumentType,
    description: 'Standard operating procedures for all production processes',
    fileName: 'sop-production-manual.pdf',
    fileSize: 4194304, // 4 MB
    mimeType: 'application/pdf',
    url: '/mock/vault/sop-production-manual.pdf',
    currentVersion: '4.2',
    versions: [
      {
        version: '4.2',
        uploadedAt: new Date('2024-12-01T09:00:00'),
        uploadedBy: 'user-002',
        uploadedByName: 'Siti Aminah',
        fileSize: 4194304,
        url: '/mock/vault/sop-production-manual.pdf',
        changes: 'Updated cleaning procedures',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'COMPANY' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2024-12-01T09:00:00'),
    uploadedBy: 'user-002',
    uploadedByName: 'Siti Aminah',
    verified: true,
    verifiedBy: 'user-003',
    verifiedByName: 'Dr. Zainab Ali',
    verifiedAt: new Date('2024-12-02T11:00:00'),
    tags: ['sop', 'procedures', 'production', 'operations'],
    createdAt: new Date('2023-01-15T08:00:00'),
    updatedAt: new Date('2024-12-02T11:00:00'),
  },

  // Training
  {
    id: 'doc-vault-006',
    name: 'Employee Training Records 2024',
    category: 'TRAINING' as DocumentCategory,
    type: 'TRAINING_RECORDS' as DocumentType,
    description: 'Halal awareness and executive training attendance',
    fileName: 'training-records-2024.xlsx',
    fileSize: 327680, // 320 KB
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    url: '/mock/vault/training-records-2024.xlsx',
    currentVersion: '1.0',
    versions: [
      {
        version: '1.0',
        uploadedAt: new Date('2024-12-15T16:00:00'),
        uploadedBy: 'user-002',
        uploadedByName: 'Siti Aminah',
        fileSize: 327680,
        url: '/mock/vault/training-records-2024.xlsx',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'ADMIN_ONLY' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2024-12-15T16:00:00'),
    uploadedBy: 'user-002',
    uploadedByName: 'Siti Aminah',
    verified: true,
    verifiedBy: 'user-003',
    verifiedByName: 'Dr. Zainab Ali',
    verifiedAt: new Date('2024-12-16T09:00:00'),
    tags: ['training', 'records', 'employees', 'compliance'],
    createdAt: new Date('2024-12-15T16:00:00'),
    updatedAt: new Date('2024-12-16T09:00:00'),
  },

  // Suppliers
  {
    id: 'doc-vault-007',
    name: 'Supplier Halal Certificates - Q1 2025',
    category: 'SUPPLIERS' as DocumentCategory,
    type: 'SUPPLIER_CERTIFICATES' as DocumentType,
    description: 'Compiled halal certificates from all approved suppliers',
    fileName: 'supplier-halal-certs-q1-2025.zip',
    fileSize: 8388608, // 8 MB
    mimeType: 'application/zip',
    url: '/mock/vault/supplier-halal-certs-q1-2025.zip',
    currentVersion: '1.0',
    versions: [
      {
        version: '1.0',
        uploadedAt: new Date('2025-01-10T11:00:00'),
        uploadedBy: 'user-002',
        uploadedByName: 'Siti Aminah',
        fileSize: 8388608,
        url: '/mock/vault/supplier-halal-certs-q1-2025.zip',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'COMPANY' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2025-01-10T11:00:00'),
    uploadedBy: 'user-002',
    uploadedByName: 'Siti Aminah',
    expiryDate: new Date('2025-03-31T23:59:59'),
    reminderDays: 14,
    verified: true,
    verifiedBy: 'user-003',
    verifiedByName: 'Dr. Zainab Ali',
    verifiedAt: new Date('2025-01-11T10:00:00'),
    tags: ['suppliers', 'certificates', 'halal', 'compliance'],
    createdAt: new Date('2025-01-10T11:00:00'),
    updatedAt: new Date('2025-01-11T10:00:00'),
  },

  // Products
  {
    id: 'doc-vault-008',
    name: 'Product Ingredient Specifications',
    category: 'PRODUCTS' as DocumentCategory,
    type: 'INGREDIENT_LIST' as DocumentType,
    description: 'Detailed ingredient list for all product lines',
    fileName: 'ingredient-specifications.pdf',
    fileSize: 1572864, // 1.5 MB
    mimeType: 'application/pdf',
    url: '/mock/vault/ingredient-specifications.pdf',
    currentVersion: '2.3',
    versions: [
      {
        version: '2.3',
        uploadedAt: new Date('2024-11-20T13:00:00'),
        uploadedBy: 'user-002',
        uploadedByName: 'Siti Aminah',
        fileSize: 1572864,
        url: '/mock/vault/ingredient-specifications.pdf',
        changes: 'Updated palm oil supplier',
      },
    ],
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    accessLevel: 'CONFIDENTIAL' as DocumentAccessLevel,
    status: 'ACTIVE' as DocumentStatus,
    uploadedAt: new Date('2024-11-20T13:00:00'),
    uploadedBy: 'user-002',
    uploadedByName: 'Siti Aminah',
    verified: true,
    verifiedBy: 'user-003',
    verifiedByName: 'Dr. Zainab Ali',
    verifiedAt: new Date('2024-11-21T09:00:00'),
    tags: ['ingredients', 'products', 'specifications', 'formulation'],
    createdAt: new Date('2023-05-10T09:00:00'),
    updatedAt: new Date('2024-11-21T09:00:00'),
  },
];

/**
 * Helper functions
 */

export function getDocumentById(id: string): Document | undefined {
  return mockDocuments.find((doc) => doc.id === id);
}

export function getDocumentsByCategory(category: DocumentCategory): Document[] {
  return mockDocuments.filter((doc) => doc.category === category);
}

export function getDocumentsByCompany(companyId: string): Document[] {
  return mockDocuments.filter((doc) => doc.companyId === companyId);
}

export function getDocumentsByStatus(status: DocumentStatus): Document[] {
  return mockDocuments.filter((doc) => doc.status === status);
}

export function getExpiringDocuments(daysAhead: number = 30): Document[] {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + daysAhead);

  return mockDocuments.filter((doc) => {
    if (!doc.expiryDate) return false;
    return doc.expiryDate >= today && doc.expiryDate <= futureDate;
  });
}

export function getExpiredDocuments(): Document[] {
  const today = new Date();
  return mockDocuments.filter((doc) => {
    if (!doc.expiryDate) return false;
    return doc.expiryDate < today;
  });
}

export function getUnverifiedDocuments(): Document[] {
  return mockDocuments.filter((doc) => !doc.verified);
}

export function searchDocumentsByTag(tag: string): Document[] {
  return mockDocuments.filter((doc) => doc.tags.includes(tag.toLowerCase()));
}

export function getDocumentStatistics() {
  return {
    total: mockDocuments.length,
    active: getDocumentsByStatus('ACTIVE' as DocumentStatus).length,
    expired: getExpiredDocuments().length,
    expiringSoon: getExpiringDocuments(30).length,
    unverified: getUnverifiedDocuments().length,
    byCategory: {
      licenses: getDocumentsByCategory('LICENSES' as DocumentCategory).length,
      policies: getDocumentsByCategory('POLICIES' as DocumentCategory).length,
      certifications: getDocumentsByCategory('CERTIFICATIONS' as DocumentCategory).length,
      facility: getDocumentsByCategory('FACILITY' as DocumentCategory).length,
      training: getDocumentsByCategory('TRAINING' as DocumentCategory).length,
      suppliers: getDocumentsByCategory('SUPPLIERS' as DocumentCategory).length,
      products: getDocumentsByCategory('PRODUCTS' as DocumentCategory).length,
      sops: getDocumentsByCategory('SOPs' as DocumentCategory).length,
    },
  };
}
